'use client';

import type { BannerButtonImageContainer } from '@bw/apis/contentful';
import { parseIdFromYoutubeUrl } from '@bw/core';
import { Image } from '@bw/elements';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, Document, INLINES } from '@contentful/rich-text-types';
import { YouTubeEmbed } from '@next/third-parties/google';
import React from 'react';
import { ThemeColor } from '../../theme';
import { CalendlyButton } from '../CalendlyButton/CalendlyButton';
import { useAppContext } from '../form-stepper/StepperProvider';
import { PdfViewerButton } from '../pdf-viewer-button/PdfViewerButton';
import { StepperButton } from '../stepper-button/StepperButton';
import { Button } from '../utility/button/button';
import styles from './hero-banner.module.scss';

type AnnouncementHeightProps = Record<string, string>;

export function HeroBannerClient({
	fields,
}: { fields: BannerButtonImageContainer['fields'] }) {
	const { announcementBannerHeight } = useAppContext();

	const options = {
		renderNode: {
			[BLOCKS.PARAGRAPH]: (node: any, children: React.ReactNode) => {
				return <h1 data-aos="fade-up">{children}</h1>;
			},
		},
	};

	const options2 = {
		renderNode: {
			[BLOCKS.PARAGRAPH]: (node: any, children: React.ReactNode) => {
				return <p data-aos="fade-up">{children}</p>;
			},
			[INLINES.HYPERLINK]: (node: any) => {
				return (
					<a
						href={node.data.uri}
						title={node.content[0].value}
						target="_blank"
						rel="noopener noreferrer"
					>
						{node.content[0].value}
					</a>
				);
			},
			[BLOCKS.LIST_ITEM]: (node: any, children: React.ReactNode) => {
				return (
					<li className={styles.address} data-aos="fade-up">
						{children}
					</li>
				);
			},
		},
	};

	const buttonData = fields.button;
	const textData = fields.textImageSection;
	const imageSection = textData?.fields.sectionImage;
	const imageUrl = imageSection?.fields.file?.url;
	const imageTitle = imageSection?.fields.title;
	const imageWidth = imageSection?.fields.file?.details?.image?.width;
	const imageHeight = imageSection?.fields.file?.details?.image?.height;
	const heading = textData?.fields.heading as Document;
	const richTextDescription = textData?.fields.richTextDescription as Document;
	const videoUrl = textData?.fields.youtubeUrl;
	const videoId = videoUrl ? parseIdFromYoutubeUrl(videoUrl) : null;
	const imagePosition = textData?.fields.imagePosition;
	const equalWidth = textData?.fields.equalWidth;
	const mobileImagePosition = textData?.fields.mobileImagePosition;
	const imageBackgroundPosition = textData?.fields.imageBackgroundPosition;
	const headingTagline = textData?.fields.headingTagline;
	const subtitle = textData?.fields.headingTagline;

	const announcementHeight: AnnouncementHeightProps = {
		'--announcement-banner-height': `${announcementBannerHeight}px`,
	};

	return (
		<div
			className={`${styles.heroBanner}
				${imagePosition === 'Right' ? styles['heroBanner--imagePosition'] : ''}
				${equalWidth ? styles['heroBanner--equalWidth'] : ''}
				${fields.backgroundColour ? styles['heroBanner--' + fields.backgroundColour] : ''}
				${fields.isFirstComponent ? styles['heroBanner--firstComponent'] : ''}
				${mobileImagePosition === 'Below Text' ? styles['heroBanner--imageBelowText'] : ''}
				`}
			style={announcementHeight}
			data-aos="fade-up"
		>
			{imageUrl && (
				<figure className={styles.imageContent} data-aos="fade-up">
					<Image
						src={imageUrl}
						alt={imageTitle ?? ''}
						width={imageWidth}
						height={imageHeight}
						sizes="(max-width: 1023px) 100vw, 42vw"
						style={{ objectPosition: imageBackgroundPosition }}
						priority
					/>
				</figure>
			)}

			{videoId && (
				<figure className={styles.videoContent} data-aos="fade-up">
					<YouTubeEmbed videoid={videoId} params="controls=0" />
				</figure>
			)}

			<article className={`${styles.textContent}`}>
				{headingTagline && <span data-aos="fade-up">{headingTagline}</span>}

				{heading && documentToReactComponents(heading, options)}

				{/* This seems to be ignored on purpose */}
				{/* {subtitle && <p data-aos="fade-up">{subtitle}</p>} */}

				{richTextDescription &&
					documentToReactComponents(richTextDescription, options2)}
				<div className={styles.ctaSection}>
					{fields.containsStartAQuoteButton && (
						<StepperButton title="Start a quote" paletteVariant="primary" />
					)}
					{fields.containsBookACallButton && (
						<CalendlyButton title="Book a call" paletteVariant="primary" />
					)}
					{buttonData &&
						buttonData.map((btn, index) => {
							const title = btn?.fields.title;
							const buttonUrl = btn?.fields.buttonUrl;
							const textColour = btn?.fields.textColour ?? 'black';
							const backgroundColour = btn?.fields.backgroundColour ?? 'none';
							const borderColour = btn?.fields.borderColour ?? 'black';
							const type = btn?.fields.type;
							const openInNewTab = btn?.fields.openInNewTab;
							if (buttonUrl?.includes('calendly.com')) {
								return (
									<CalendlyButton
										title={title}
										calendlyUrl={buttonUrl}
										key={index}
									/>
								);
							}
							if (buttonUrl?.endsWith('.pdf')) {
								return (
									<PdfViewerButton
										key={index}
										title={title ?? ''}
										href={buttonUrl}
										textColor={textColour as ThemeColor}
										backgroundColor={backgroundColour as ThemeColor}
										borderColor={borderColour as ThemeColor}
										type={type}
										openInNewTab={openInNewTab}
									/>
								);
							}
							return (
								<Button
									title={title ?? ''}
									href={buttonUrl}
									textColor={textColour as ThemeColor}
									backgroundColor={backgroundColour as ThemeColor}
									borderColor={borderColour as ThemeColor}
									type={type}
									openInNewTab={openInNewTab}
									key={index}
									data-aos="fade-up"
								/>
							);
						})}
				</div>
			</article>
		</div>
	);
}
